import { useMemo, useState } from 'react';
import type { PreviewResponse } from '@lib/services/padrones.service';

interface Props {
  preview: PreviewResponse;
  onCancel: () => void;
  onConfirm: (mapping: Record<string, number>) => void | Promise<void>;
  submitting?: boolean;
  serverError?: string | null;
}

const UNMAPPED = -1;

export function ColumnMappingModal({
  preview,
  onCancel,
  onConfirm,
  submitting,
  serverError,
}: Props) {
  const [mapping, setMapping] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    preview.campos.forEach((c) => {
      const detected = preview.detectedMapping[c.key];
      initial[c.key] = typeof detected === 'number' ? detected : UNMAPPED;
    });
    return initial;
  });

  const headerOptions = useMemo(
    () =>
      preview.headers.map((h, i) => ({
        idx: i,
        label: h && h.trim() ? h : `(columna ${i + 1})`,
      })),
    [preview.headers],
  );

  // Detecta duplicados de columna (la misma columna asignada a más de un campo)
  const usedColumns = useMemo(() => {
    const counts = new Map<number, number>();
    Object.values(mapping).forEach((v) => {
      if (v >= 0) counts.set(v, (counts.get(v) ?? 0) + 1);
    });
    return counts;
  }, [mapping]);

  const requeridosFaltantes = preview.campos
    .filter((c) => c.requerido && mapping[c.key] === UNMAPPED)
    .map((c) => c.label);

  const duplicados = Array.from(usedColumns.entries()).filter(([, n]) => n > 1);

  const isValid = requeridosFaltantes.length === 0 && duplicados.length === 0;

  function handleSelect(key: string, value: string): void {
    setMapping((m) => ({ ...m, [key]: Number(value) }));
  }

  async function handleConfirm() {
    const final: Record<string, number> = {};
    Object.entries(mapping).forEach(([k, v]) => {
      if (v >= 0) final[k] = v;
    });
    await onConfirm(final);
  }

  return (
    <div className="modalBackdrop" onClick={onCancel}>
      <div
        className="modalCard"
        style={{ maxWidth: 900 }}
        onClick={(e) => e.stopPropagation()}
      >
        <header style={{ marginBottom: 16 }}>
          <h2 className="heading-3">Confirmar mapeo de columnas</h2>
          <p className="text-element-mid" style={{ marginTop: 4 }}>
            Archivo: <strong>{preview.filename}</strong> · {preview.totalDataRows.toLocaleString('es-AR')} filas
            de datos · {preview.headers.length} columnas detectadas
          </p>
          <p className="text-element-mid" style={{ fontSize: 13 }}>
            Asociá cada campo interno a la columna correspondiente del archivo. Los campos en
            <span className="status status-error" style={{ marginInline: 6 }}>rojo</span>
            son obligatorios.
          </p>
        </header>

        {preview.desconocidas.length > 0 && (
          <p className="status status-warning" style={{ marginBottom: 12, display: 'block' }}>
            Columnas no reconocidas (se ignorarán si no las usás): {preview.desconocidas.join(', ')}
          </p>
        )}

        <div className="tableWrap" style={{ marginBottom: 16 }}>
          <table className="dataTable">
            <thead>
              <tr>
                <th style={{ width: 220 }}>Campo</th>
                <th>Columna del archivo</th>
                <th>Vista previa (3 filas)</th>
              </tr>
            </thead>
            <tbody>
              {preview.campos.map((c) => {
                const selected = mapping[c.key];
                const isDup = selected >= 0 && (usedColumns.get(selected) ?? 0) > 1;
                const missing = c.requerido && selected === UNMAPPED;
                return (
                  <tr key={c.key} className={missing ? 'rowError' : undefined}>
                    <td>
                      <strong>{c.label}</strong>
                      {c.requerido && (
                        <span
                          className="status status-error"
                          style={{ marginLeft: 8, fontSize: 11 }}
                        >
                          obligatorio
                        </span>
                      )}
                    </td>
                    <td>
                      <select
                        className="input"
                        value={selected}
                        onChange={(e) => handleSelect(c.key, e.target.value)}
                        style={{
                          minWidth: 240,
                          borderColor: missing ? 'var(--color-error)' : undefined,
                        }}
                      >
                        <option value={UNMAPPED}>— Sin mapear —</option>
                        {headerOptions.map((h) => (
                          <option key={h.idx} value={h.idx}>
                            {h.label}
                          </option>
                        ))}
                      </select>
                      {isDup && (
                        <p className="text-element-mid" style={{ color: 'var(--color-error)', fontSize: 11, marginTop: 4 }}>
                          Esta columna ya está asignada a otro campo
                        </p>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--color-element-mid)', maxWidth: 280 }}>
                      {selected >= 0
                        ? preview.sampleRows
                            .map((row) => row[selected] ?? '')
                            .filter((v) => v && v.trim() !== '')
                            .slice(0, 3)
                            .map((v, i) => <div key={i}>{v}</div>)
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!isValid && requeridosFaltantes.length > 0 && (
          <p className="status status-error" style={{ display: 'block', marginBottom: 8 }}>
            Falta mapear: {requeridosFaltantes.join(', ')}
          </p>
        )}
        {serverError && (
          <p className="status status-error" style={{ display: 'block', marginBottom: 8 }}>
            {serverError}
          </p>
        )}

        <div className="rowBetween" style={{ marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={submitting}>
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            disabled={!isValid || submitting}
            onClick={handleConfirm}
          >
            <span className="btn-font-style">
              {submitting ? 'Procesando…' : 'Procesar padrón'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
